/*
 * "Ease anything that moves" (sections/easeflow-props.liquid). One Program
 * monitor, four tabs: Position, Scale, Opacity, Volume. Each tab plays its
 * property there and back, linear (grey) against eased (blue); Position
 * follows the curved motion path drawn in the monitor. While on screen the
 * tabs play through by themselves; clicking a tab (or the monitor) hands
 * it over - that tab keeps looping - and Replay restarts the tour. With
 * reduced motion each tab shows a still frame mid-move.
 */
(() => {
  const still = window.matchMedia('(prefers-reduced-motion: reduce)');

  const bezier = (x1, y1, x2, y2) => {
    const at = (a, b, t) => 3 * a * t * (1 - t) * (1 - t) + 3 * b * t * t * (1 - t) + t * t * t;
    return (x) => {
      if (x <= 0) return 0;
      if (x >= 1) return 1;
      let lo = 0;
      let hi = 1;
      for (let i = 0; i < 26; i += 1) {
        const mid = (lo + hi) / 2;
        if (at(x1, x2, mid) < x) lo = mid;
        else hi = mid;
      }
      return at(y1, y2, (lo + hi) / 2);
    };
  };
  const ease = bezier(0.65, 0, 0.35, 1);

  // one round of a tab: a beat, there, a hold, back, a hold (ms)
  const LEAD = 180;
  const MOVE = 1250;
  const HOLD = 450;
  const ROUND = LEAD + MOVE + HOLD + MOVE + HOLD;
  const progress = (t) => {
    if (t < LEAD) return 0;
    if (t < LEAD + MOVE) return (t - LEAD) / MOVE;
    if (t < LEAD + MOVE + HOLD) return 1;
    if (t < LEAD + MOVE * 2 + HOLD) return 1 - (t - LEAD - MOVE - HOLD) / MOVE;
    return 0;
  };
  const VIEW_W = 640;
  const VIEW_H = 360;

  const setup = (section) => {
    if (section.dataset.epReady) return;
    section.dataset.epReady = '1';
    const $ = (name, root = section) => root.querySelector(`[data-ep="${name}"]`);
    const stage = $('stage');
    const monitor = $('monitor');
    if (!stage || !monitor) return;
    const tabs = Array.from(section.querySelectorAll('[data-ep="tab"]'));
    const scenes = Array.from(section.querySelectorAll('[data-ep="scene"]'));
    const thumb = $('thumb');
    const caption = $('caption');
    const replay = $('replay');
    const path = $('path');
    const props = tabs.map((tab) => tab.dataset.prop);
    const parts = {};
    scenes.forEach((scene) => {
      parts[scene.dataset.prop] = { scene, ghost: $('ghost', scene), clip: $('clip', scene) };
    });

    // the motion path, sampled by length so the linear clip moves at an even speed
    let samples = [];
    if (path && path.getTotalLength) {
      const total = path.getTotalLength();
      for (let i = 0; i <= 120; i += 1) {
        const p = path.getPointAtLength((total * i) / 120);
        samples.push([p.x / VIEW_W, p.y / VIEW_H]);
      }
    }
    if (!samples.length) samples = [[0.19, 0.74], [0.81, 0.42]];
    const pointAt = (x) => {
      const f = Math.max(0, Math.min(1, x)) * (samples.length - 1);
      const i = Math.min(samples.length - 2, Math.floor(f));
      const k = f - i;
      const a = samples[i];
      const b = samples[i + 1] || a;
      return [a[0] + (b[0] - a[0]) * k, a[1] + (b[1] - a[1]) * k];
    };

    let width = monitor.clientWidth;
    let height = monitor.clientHeight;

    const paint = (prop, el, v) => {
      if (!el) return;
      if (prop === 'position') {
        const [px, py] = pointAt(v);
        el.style.transform = `translate3d(${(px * width).toFixed(1)}px, ${(py * height).toFixed(1)}px, 0) translate(-50%, -50%)`;
      } else if (prop === 'scale') {
        el.style.transform = `scale(${(0.32 + 0.68 * v).toFixed(4)})`;
      } else if (prop === 'opacity') {
        el.style.opacity = (0.04 + 0.96 * v).toFixed(3);
      } else if (prop === 'volume') {
        el.style.transform = `scaleY(${(0.1 + 0.9 * v).toFixed(4)})`;
      }
    };

    let current = 0;
    let x = 0;
    const draw = () => {
      const prop = props[current];
      const part = parts[prop];
      if (!part) return;
      paint(prop, part.ghost, x);
      paint(prop, part.clip, ease(x));
    };

    const select = (index) => {
      current = index;
      tabs.forEach((tab, i) => {
        const on = i === index;
        tab.setAttribute('aria-selected', on ? 'true' : 'false');
        tab.tabIndex = on ? 0 : -1;
      });
      monitor.setAttribute('aria-labelledby', tabs[index].id);
      if (thumb) thumb.style.setProperty('--ep-at', index);
      scenes.forEach((scene) => scene.classList.toggle('is-on', scene.dataset.prop === props[index]));
      if (caption) caption.textContent = caption.dataset[props[index]] || '';
    };

    // lay every scene out at its start, so a tab never shows a stale frame
    const reset = (value) => {
      const keep = current;
      props.forEach((prop, i) => {
        current = i;
        x = value;
        draw();
      });
      current = keep;
    };

    if ('ResizeObserver' in window) {
      new ResizeObserver(() => {
        width = monitor.clientWidth;
        height = monitor.clientHeight;
        draw();
        const pos = parts.position;
        if (pos && props[current] !== 'position') {
          paint('position', pos.ghost, still.matches ? 0.4 : 0);
          paint('position', pos.clip, ease(still.matches ? 0.4 : 0));
        }
      }).observe(monitor);
    }

    let touring = !still.matches;
    let running = false;
    let raf = 0;
    let last = 0;
    let clock = 0;

    const tick = (now) => {
      raf = 0;
      if (!running) return;
      const dt = last ? Math.min(50, now - last) : 16;
      last = now;
      clock += dt;
      if (clock >= ROUND) {
        clock %= ROUND;
        if (touring) {
          select((current + 1) % props.length);
          reset(0);
        }
      }
      x = progress(clock);
      draw();
      raf = requestAnimationFrame(tick);
    };

    const start = () => {
      if (running || still.matches) return;
      running = true;
      last = 0;
      raf = requestAnimationFrame(tick);
    };
    const stop = () => {
      running = false;
      if (raf) cancelAnimationFrame(raf);
      raf = 0;
    };

    const handOver = () => {
      if (!touring) return;
      touring = false;
      stage.classList.add('is-yours');
    };

    const choose = (index) => {
      handOver();
      if (index === current && !still.matches) return;
      select(index);
      if (still.matches) {
        x = 0.4;
        draw();
        return;
      }
      clock = 0;
      reset(0);
      draw();
    };

    tabs.forEach((tab, i) => {
      tab.addEventListener('click', () => choose(i));
      tab.addEventListener('keydown', (event) => {
        let next = -1;
        if (event.key === 'ArrowRight' || event.key === 'ArrowDown') next = (i + 1) % tabs.length;
        else if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') next = (i - 1 + tabs.length) % tabs.length;
        else if (event.key === 'Home') next = 0;
        else if (event.key === 'End') next = tabs.length - 1;
        if (next < 0) return;
        event.preventDefault();
        tabs[next].focus();
        choose(next);
      });
    });
    monitor.addEventListener('pointerdown', handOver);
    if (replay) {
      replay.addEventListener('click', () => {
        touring = true;
        stage.classList.remove('is-yours');
        select(0);
        clock = 0;
        reset(0);
        draw();
        update();
      });
    }

    select(0);
    if (still.matches) {
      stage.classList.remove('is-yours');
      reset(0.4);
      return;
    }
    reset(0);

    let visible = false;
    const update = () => (visible && !document.hidden ? start() : stop());
    if ('IntersectionObserver' in window) {
      new IntersectionObserver((entries) => {
        visible = entries[entries.length - 1].isIntersecting;
        update();
      }, { threshold: 0.2 }).observe(monitor);
    } else {
      visible = true;
    }
    document.addEventListener('visibilitychange', update);
    update();
  };

  const init = (root) => root.querySelectorAll('[data-ef-props]').forEach(setup);
  init(document);
  document.addEventListener('shopify:section:load', (event) => init(event.target));
})();
